import { useRef, useState } from "react";

import { t } from "../i18n";
import { Icons } from "../lib/icons";
import type { Datapoint, FilesState, Locale } from "../types";

interface Props {
  datapoint: Datapoint;
  locale: Locale;
  busy: boolean;
  error: string | null;
  onUpload: (file: File) => void;
  /** Postęp pytania wieloplikowego (max>1). Brak = pole jednoplikowe. */
  files?: FilesState | null;
  /** „Gotowe" — kandydat potwierdza komplet stron. */
  onFinish?: () => void;
}

/**
 * Na telefonie dokument fotografuje się w miejscu — `capture` otwiera aparat od razu,
 * bez wycieczki do galerii. Drugi przycisk zostaje dla plików już zapisanych (PDF).
 */
export function FileInput({ datapoint, locale, busy, error, onUpload, files, onFinish }: Props) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<string | null>(null);

  const handle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // ten sam plik można wybrać ponownie po błędzie
    if (!file) return;
    setPicked(file.name);
    onUpload(file);
  };

  // Pytanie wieloplikowe (paszport, decyzja): pokazujemy licznik stron i „Gotowe".
  // Przycisk domykający włącza się dopiero, gdy serwer zezwoli (can_finish).
  const wieloplikowe = !!files && files.max > 1;

  return (
    <>
      <div className="drop">
        <div className="drop__icon">
          <Icons.camera size={26} />
        </div>
        <p className="drop__hint">{datapoint.placeholder ?? "PDF lub zdjęcie, maks. 10 MB"}</p>
        <div className="actions">
          {/* Puls w trakcie wgrywania i OCR — czekanie, nie awaria. */}
          <button
            className={`btn btn--primary${busy ? " puls" : ""}`}
            disabled={busy}
            onClick={() => cameraRef.current?.click()}
          >
            {busy ? t("uploading", locale) : t("choosePhoto", locale)}
          </button>
          <button className="btn btn--secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
            {t("chooseFile", locale)}
          </button>
        </div>
        {picked && !error && <p className="field__hint">{picked}</p>}
      </div>

      <input
        ref={cameraRef}
        className="sr-only"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handle}
        tabIndex={-1}
        aria-hidden="true"
      />
      <input
        ref={fileRef}
        className="sr-only"
        type="file"
        accept="image/*,application/pdf"
        onChange={handle}
        tabIndex={-1}
        aria-hidden="true"
      />

      {wieloplikowe && (
        <div className="files-progress">
          {files!.uploaded > 0 && (
            <p className="field__hint">
              {t("filesUploadedCount", locale)} {files!.uploaded}
              {files!.max < 99 ? ` / ${files!.max}` : ""}
            </p>
          )}
          {!files!.can_finish && <p className="field__hint">{t("filesNeedMore", locale)}</p>}
          {files!.can_finish && onFinish && (
            <button className="btn btn--primary" disabled={busy} onClick={onFinish}>
              {t("filesDone", locale)}
            </button>
          )}
        </div>
      )}

      {datapoint.hint && <p className="field__hint">{datapoint.hint}</p>}
      {error && <p className="error">{error}</p>}
    </>
  );
}
